import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import moment from 'moment'

import EmptyCard from './EmptyCard'
import InstallHostInfoRow from './InstallHostInfoRow'

import { getColorForClientState } from 'utils/colors.ts'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import CardWrapper from 'components/dashboard/CardWrapper'
import BusyOverlay from 'components/common/BusyOverlay'
import Text from 'components/common/Text'
import Button from 'components/common/Button'
import { getClientsLastAttackedDateMap } from 'components/attacks/create/targets/target-utils'
import { generateRerunAttackLinkTo } from 'components/attacks/utils/rerun'
import useMetadata from 'hooks/use-metadata'
import theme from 'theme'

function ClientRowWrapper(props) {
  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: 'minmax(256px, 1fr) auto',
        alignItems: 'center',
        gap: theme.space[5],
        paddingTop: theme.space[2],
        paddingBottom: theme.space[2],
        paddingRight: theme.space[3],
        paddingLeft: theme.space[3],
        fontSize: theme.fontSizes[0],
        color: theme.colors.steel[1],
        borderBottom: theme.border.base,
        height: theme.space[7]
      }}
      {...props}
    />
  )
}

export function DashboardClientTag({ label, clientState }) {
  return (
    <div
      css={{
        background: getColorForClientState(clientState),
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        borderRadius: theme.borderRadius[0],
        fontSize: theme.fontSizes[0],
        padding: theme.space[0],
        paddingRight: theme.space[2],
        paddingLeft: theme.space[2]
      }}
    >
      <Text
        as="h6"
        css={{
          color: theme.colors.white[0]
        }}
      >
        {label}
      </Text>
    </div>
  )
}

DashboardClientTag.propTypes = {
  label: PropTypes.string,
  clientState: PropTypes.string
}

function Targets({ originalClients, filter, attacks }) {
  const { metadata } = useMetadata()
  const history = useHistory()

  const hasUserInstalledClients =
    [].concat(
      originalClients.active,
      originalClients.idle,
      originalClients.inactive
    ).length > 0

  const filteredClients = useMemo(() => {
    if (filter !== 'all') {
      return originalClients[filter] || []
    } else {
      return Object.values(originalClients)
        .flat()
        .filter(client => client)
    }
  }, [originalClients, filter])

  const clientsLastAttackedDateMap = useMemo(() => {
    return getClientsLastAttackedDateMap({ attacks, clients: filteredClients })
  }, [attacks, filteredClients])

  if (Object.values(originalClients).flat().length === 0) {
    return (
      <EmptyCard title="No Targets">
        <Button
          as={TrackedExternalLink}
          href="https://www.gremlin.com/docs/application-layer/installation/"
          label="Install a Gremlin host"
        />
      </EmptyCard>
    )
  }

  if (filteredClients.length === 0) {
    return (
      <Text
        as="p"
        css={{
          padding: theme.space[5]
        }}
      >
        {' '}
        No clients Match the description
      </Text>
    )
  }

  return (
    <div>
      {filteredClients.map(client => {
        const lastAttackedTimestamp =
          clientsLastAttackedDateMap[client.identifier]
        return (
          <ClientRowWrapper key={client.identifier}>
            <div
              css={{
                display: 'grid',
                columnGap: theme.space[3],
                rowGap: theme.space[1],
                gridTemplateColumns: `${theme.space[7]}px 1fr`,
                alignItems: 'center'
              }}
            >
              <DashboardClientTag
                clientState={client.state}
                label={client.state}
              />
              <div>
                <Text as="h5">{client.identifier}</Text>
                <div>
                  {lastAttackedTimestamp
                    ? `Last Attacked: ${moment(
                        lastAttackedTimestamp
                      ).fromNow()}`
                    : ''}
                </div>
              </div>
            </div>
            {client.state !== 'ACTIVE' ? null : (
              <Button
                secondary
                css={{
                  justifyContent: 'center',
                  padding: theme.space[2],
                  paddingLeft: theme.space[3],
                  paddingRight: theme.space[3]
                }}
                label="Attack"
                onClick={() => {
                  history.push(
                    generateRerunAttackLinkTo(
                      {
                        infra_target: {
                          resolvedHosts: [client.identifier],
                          targetType: 'Host',
                          strategyType: 'Exact'
                        },
                        target_type: 'Host',
                        targets: [client.identifier],
                        total_clients: 1,
                        targetAll: false
                      },
                      metadata
                    )
                  )
                }}
              />
            )}
          </ClientRowWrapper>
        )
      })}
      {!hasUserInstalledClients && (
        <ClientRowWrapper key="install_your_own_host_target_row">
          <InstallHostInfoRow />
        </ClientRowWrapper>
      )}
    </div>
  )
}

Targets.propTypes = {
  originalClients: PropTypes.object,
  filter: PropTypes.string,
  attacks: PropTypes.array
}

export default function TargetsCard({ allAttacks, clientResource }) {
  const {
    infraClients,
    alfiClients,
    alfiIsLoading,
    infraIsLoading
  } = clientResource

  const isLoading = alfiIsLoading || infraIsLoading

  const originalClients = useMemo(() => {
    const allClients = [].concat(infraClients, alfiClients)
    return {
      active: allClients.filter(c => c.state === 'ACTIVE'),
      idle: allClients.filter(c => c.state === 'IDLE'),
      inactive: allClients.filter(c => c.state === 'INACTIVE')
    }
  }, [infraClients, alfiClients])

  return (
    <CardWrapper
      defaultFilter="all"
      extraText={
        <Text
          as="h6"
          css={{
            fontWeight: theme.fontWeights[0]
          }}
        >
          Provisioned: <strong>50/day</strong>
        </Text>
      }
      filters={[
        { value: 'all', label: 'View All' },
        { value: 'active', label: 'Active' },
        { value: 'idle', label: 'Idle' },
        { value: 'inactive', label: 'Inactive' }
      ]}
      hideFilter={isLoading}
      renderCardContent={({ filter }) => {
        return isLoading ? (
          <BusyOverlay rounded shown />
        ) : (
          <Targets
            attacks={allAttacks}
            filter={filter}
            originalClients={originalClients}
          />
        )
      }}
      title="Targets"
    />
  )
}

TargetsCard.propTypes = {
  allAttacks: PropTypes.array,
  attacksLoading: PropTypes.bool,
  clientResource: PropTypes.object
}
